import React, { useRef, useEffect, useState, CSSProperties, useLayoutEffect, useMemo } from 'react'
import {
  CellProps,
  Column,
  HeaderGroup,
  HeaderProps,
  IdType,
  Row,
  TableState,
  useColumnOrder,
  useFilters,
  useFlexLayout,
  usePagination,
  useResizeColumns,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import { useVirtualizer } from '@tanstack/react-virtual'

import Box from 'components/Box'
import IndeterminateCheckBox from '../Checkboxes/IndeterminateCheckBox'
import Text from '../Text'
import Skeleton from '../SkeletonLoader'

import { FixMeAny, UnknownRecord } from 'types/global'
import { ExtendedError } from 'store/types'
import { getColumnDragAndDropObject, getReorderedColumns, isInlineTable, resolveTableHeight } from './helpers'
import { StyledCell, StyledTable, StyledRow } from './styles'

import EmptyResultView from './EmptyResultView'
import BulkOperationPanel, { BulkOperationPanelProps, BulkOperationAction as BulkAction } from './BulkOperationPanel'
import TableHeader, { SorterStatus } from './TableHeader'
import Pagination, { PageNumberActions, PageNumberActionType } from './Pagination'
import ErrorView from 'components/ErrorView'

export type { SorterStatus }
export type BulkOperationAction = BulkAction
export type SelectedRowIdsType<D extends UnknownRecord> = Record<IdType<D>, boolean>
export type TableColumn<D extends Record<string, unknown>> = Column<D> & {
  className?: string
  headerClassName?: string
}

export type TableSize = 'small' | 'medium' | 'large'

export enum TableVariantType {
  default = 'default',
  inline = 'inline-table',
}

export type TableVariant = typeof TableVariantType[keyof typeof TableVariantType]

export type TableProps<D extends UnknownRecord> = BulkOperationPanelProps & {
  columns?: Array<TableColumn<D>>
  data?: Array<D>
  columnWidths?: ColumnWidthType
  emptyView?: React.ReactNode
  emptyText?: string
  onChangePagination?: (pagination: PaginationType) => void
  onRowClick?: (row: D) => void
  onSelectRow?: (row: Row<D>[], selectedRowIds?: SelectedRowIdsType<D>, flatRows?: Row<D>[]) => void
  onClearRows?: () => void
  // Only Use this attribute to preserve row selection for server side paginated tables
  preSelectedIds?: { [key: number]: boolean }
  // Only Use this attribute to pass the selected row count for server side paginated tables
  selectedRowCount?: number
  pageNumber?: number
  pageSize?: number
  serverSide?: boolean
  totalDataCount?: number
  withCheckbox?: boolean
  uniqueTableName: string
  isLoading?: boolean
  disablePagination?: boolean
  disableSortBy?: boolean
  showInternalFilter?: boolean
  assignHeaderGroups?: (headerGroups: HeaderGroup<D>[]) => void
  onSort?: (field: keyof D, order: SorterStatus) => void
  getTrStyles?: (row: Row<D>) => CSSProperties | undefined
  initialState?: Partial<TableState<D>>
  style?: React.CSSProperties
  size?: TableSize
  bodyHeight?: string
  isResponsiveHeight?: boolean
  // isResponsiveHeight will work only when the bottom of the table is at the bottom of the page, otherwise please use this together with offsetBottom
  offsetBottom?: number
  // fills the height of a flex ancestor (e.g. a modal body) and pins pagination to the bottom, regardless of row count
  fillContainerHeight?: boolean
  // pass true only for tables whose data arrives pre-sorted (e.g. server-side sort); false lets react-table sort client-side
  manualSortBy?: boolean
  rowHeight?: number
  borderless?: boolean
  isOnSearch?: boolean
  isFetching?: boolean
  error?: ExtendedError | null
  variant?: TableVariant
  activeRowIndex?: number
}

export type PaginationType = {
  pageNumber?: number
  pageSize?: number
}

export type ColumnWidthType = {
  [key: string]: number
}

const updateColumnWidths = <D extends UnknownRecord>(tableColumns: TableColumn<D>[], columnWidths?: ColumnWidthType) =>
  tableColumns.map((column) => {
    if (column.width || !columnWidths) return column

    const currentColumn = { ...column }

    currentColumn.width = columnWidths && column.id && columnWidths[column.id] ? columnWidths[column.id] : 150

    return currentColumn
  })

export const selectionColumn = {
  id: 'selection',
  Header: Object.assign(
    ({ getToggleAllPageRowsSelectedProps }: HeaderProps<FixMeAny>) => (
      <Box alignItems="center" display="flex" overflow="hidden">
        <IndeterminateCheckBox {...getToggleAllPageRowsSelectedProps()} />
      </Box>
    ),
    { displayName: 'headerCheckbox' },
  ),
  Cell: Object.assign(
    ({ row }: CellProps<FixMeAny>) => (
      <Box alignItems="center" display="flex" overflow="hidden">
        <IndeterminateCheckBox
          disabled={row.original.hasOwnProperty('isSelectable') ? !row.original.isSelectable : false}
          {...row.getToggleRowSelectedProps()}
        />
      </Box>
    ),
    { displayName: 'bodyCheckbox' },
  ),
  width: 50,
}

const EMPTY_TABLE_COLUMNS: [] = []
const EMPTY_TABLE_DATA: [] = []

const Table = <D extends UnknownRecord>({
  actions,
  columns: tableColumns = EMPTY_TABLE_COLUMNS,
  columnWidths,
  data: tableData = EMPTY_TABLE_DATA,
  emptyView,
  emptyText,
  isOnSearch,
  onChangePagination,
  onRowClick,
  onSelectRow,
  onClearRows,
  // Only Use this attribute to preserve row selection for server side paginated tables
  preSelectedIds = {},
  // Only Use this attribute to pass the selected row count for server side paginated tables
  selectedRowCount,
  pageNumber: currentPage = 0,
  pageSize: currentPageSize = 100,
  selectionLabel,
  serverSide = true,
  totalDataCount = 0,
  withCheckbox,
  uniqueTableName,
  isLoading,
  disablePagination,
  disableSortBy = false,
  assignHeaderGroups,
  onSort,
  getTrStyles,
  initialState = {},
  style = {},
  size = 'medium',
  bodyHeight,
  isResponsiveHeight = false,
  offsetBottom = 0,
  fillContainerHeight = false,
  manualSortBy = false,
  rowHeight,
  borderless = false,
  isFetching = false,
  error,
  activeRowIndex,
  variant = TableVariantType.default,
}: TableProps<D>) => {
  const columns = React.useMemo(() => updateColumnWidths<D>(tableColumns, columnWidths), [tableColumns, columnWidths])
  // setState is used here to fix react component state being cached issue when pass callback to DOM event listener
  const [, setColumns] = useState<FixMeAny[]>(columns)
  const [headerScroll, setHeaderScroll] = useState(0)
  const data = React.useMemo(() => (isLoading ? Array(currentPageSize).fill({}) : tableData), [
    isLoading,
    currentPageSize,
    tableData,
  ])
  const tableRef = useRef<HTMLTableElement>(null)
  const tableBodyRef = useRef<HTMLDivElement>(null)
  const theadRef = useRef<HTMLTableSectionElement>(null)

  const defaultColumn = React.useMemo(
    () => ({
      Cell: ({ cell }: FixMeAny) => <Text ellipsis>{cell.value ?? '-'}</Text>,
    }),
    [],
  )

  const {
    flatRows,
    getTableBodyProps,
    getTableProps,
    getToggleAllPageRowsSelectedProps,
    gotoPage,
    headerGroups,
    nextPage,
    page,
    prepareRow,
    previousPage,
    rows,
    selectedFlatRows,
    setColumnOrder,
    setPageSize,
    state,
    toggleAllPageRowsSelected,
    toggleAllRowsSelected,
  } = useTable<D>(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        pageIndex: 0,
        pageSize: currentPageSize,
        selectedRowIds: preSelectedIds as Record<IdType<D>, boolean>,
        ...initialState,
      },
      disableSortBy,
      ...(manualSortBy ? { manualSortBy: true } : {}),
    },
    useFlexLayout,
    useFilters,
    useSortBy,
    usePagination,
    useColumnOrder,
    useResizeColumns,
    useRowSelect,
    (hooks) => {
      if (!withCheckbox) return

      const hasCustomSelectionColumn = columns.find((column) => column.id === 'selection')
      hooks.visibleColumns.push((columns) => (hasCustomSelectionColumn ? columns : [selectionColumn, ...columns]))
    },
  )

  const bulkOperationProps = {
    actions,
    selectionLabel,
    selectedRowsCount: selectedRowCount ?? Object.keys(state.selectedRowIds).length ?? 0,
    toggleAllPageRowsSelected,
    toggleAllRowsSelected,
    getToggleAllPageRowsSelectedProps,
    onClearRows,
    totalRowsCount: data.length ?? 0,
  }

  const { lastPage, pageNumber, pageSize } = useMemo(() => {
    const pageNumber = serverSide ? currentPage : state.pageIndex + 1
    const pageSize = serverSide ? currentPageSize : state.pageSize

    return {
      lastPage: Math.ceil(totalDataCount / pageSize),
      pageNumber,
      pageSize,
    }
  }, [currentPage, currentPageSize, serverSide, state, totalDataCount])

  const rowVirtualizer = useVirtualizer({
    count: page.length,
    getScrollElement: () => tableBodyRef.current,
    estimateSize: () => rowHeight ?? 50,
  })

  const handleChangePageSize = (size: number) => {
    onChangePagination?.({ pageNumber: 1, pageSize: size })
    setPageSize(size)
  }

  const handleOnColumnChange = (selectedIndex: number, targetIndex: number) => {
    let columnIds: Array<string> = []
    setColumns((preColumns) => {
      const newHeaderData = getReorderedColumns(preColumns, selectedIndex, targetIndex)
      columnIds = newHeaderData.map((column) => column.id)
      // Update table column order
      setColumnOrder(() => columnIds)

      return newHeaderData
    })
    saveToLocalStorage(columnIds)
  }

  const handleChangePageNumber = (action: PageNumberActionType, newPageNumber = pageNumber) => {
    let tempPageNumber = newPageNumber

    switch (action) {
      case PageNumberActions.GoBack:
        if (!serverSide) {
          previousPage()
        }

        tempPageNumber = pageNumber - 1
        break
      case PageNumberActions.GoFirst:
        if (!serverSide) {
          gotoPage(0)
        }

        tempPageNumber = 1
        break
      case PageNumberActions.GoLast:
        if (!serverSide) {
          gotoPage(lastPage - 1)
        }

        tempPageNumber = lastPage
        break
      case PageNumberActions.GoNext:
        if (!serverSide) {
          nextPage()
        }

        tempPageNumber = pageNumber + 1
        break
      case PageNumberActions.GoTo:
        if (!serverSide) {
          gotoPage(newPageNumber - 1)
        }
        break
      default:
    }

    if (tempPageNumber !== pageNumber) {
      onChangePagination?.({ pageNumber: tempPageNumber, pageSize })
    }
  }

  useEffect(() => {
    const columnDragAndDrop = getColumnDragAndDropObject(tableRef, handleOnColumnChange, withCheckbox)
    setColumns(columns)

    if (columnDragAndDrop) {
      columnDragAndDrop.intiColumnDragAndDrop()
      return () => {
        columnDragAndDrop.removeColumnDragAndDropListener()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerGroups, columns])

  useEffect(() => {
    if (onSelectRow) {
      onSelectRow(selectedFlatRows, state.selectedRowIds, flatRows)
    }
  }, [selectedFlatRows.length]) //eslint-disable-line react-hooks/exhaustive-deps

  const saveToLocalStorage = (columnIds: Array<string>) => {
    // Save column order to localStorage as Array<string>
    // this should not be triggered, in case something went wrong during drag and drop columns, we should handle this.
    if (columnIds.length !== columns.length) {
      const newColumnIds = columns.map((column: FixMeAny) => column.id)
      return localStorage.setItem(`${uniqueTableName}ColumnOrder`, JSON.stringify(newColumnIds))
    }

    localStorage.setItem(`${uniqueTableName}ColumnOrder`, JSON.stringify(columnIds))
  }

  useEffect(() => {
    assignHeaderGroups?.(headerGroups)
  }, [assignHeaderGroups, headerGroups])

  const isEmpty = !isLoading && page.length === 0
  const isPaginationVisible = !disablePagination

  const getHeight = (): string => {
    const bodyOffsetTop = tableBodyRef.current?.getBoundingClientRect().top ?? 0 + window.scrollY
    const footerHeight = isPaginationVisible ? 70 : 0
    const borderWidth = borderless ? 0 : 1
    const fixedHeight = resolveTableHeight(size)
    const calculated = window.innerHeight - bodyOffsetTop - footerHeight - borderWidth - offsetBottom
    const dynamicHeight = tableBodyRef.current ? (calculated >= fixedHeight ? calculated : fixedHeight) : fixedHeight
    const resolvedHeight = isResponsiveHeight ? `${dynamicHeight}px` : bodyHeight ?? `${fixedHeight}px`
    return resolvedHeight
  }
  const [tableWidth, setTableWidth] = useState(theadRef.current?.clientWidth)

  useEffect(() => {
    if (!tableBodyRef.current) return

    const handleHanderScroll = () => {
      setHeaderScroll(tableBodyRef.current?.scrollLeft ?? 0)
    }

    const removeScrollEvent = () => {
      tableBodyRef.current?.removeEventListener('scroll', handleHanderScroll)
    }

    tableBodyRef.current?.addEventListener('scroll', handleHanderScroll)

    return () => {
      removeScrollEvent()
    }
  }, [isEmpty])

  useLayoutEffect(() => {
    const resetTableWidth = () => {
      setTableWidth(theadRef.current?.clientWidth)
    }

    if (!tableWidth) {
      resetTableWidth()
      return
    }

    window.addEventListener('resize', resetTableWidth)

    return () => {
      window.removeEventListener('resize', resetTableWidth)
    }
  }, [tableWidth, theadRef])

  const renderRow = ({ index }: { index: number }) => {
    const row = page[index]
    prepareRow(row)

    if (isLoading) {
      return (
        <>
          {row.cells.map((cell) => {
            const { key: cellKey, ...cellProps } = cell.getCellProps()
            return (
              <div key={cellKey} {...cellProps} style={{ ...cellProps.style, padding: '0.5rem' }}>
                <Skeleton />
              </div>
            )
          })}
        </>
      )
    }

    return (
      <>
        {row.cells.map((cell, cellIndex) => {
          const { key: cellKey, ...cellProps } = cell.getCellProps()
          const isCheckboxColumn = withCheckbox && cellIndex === 0

          return (
            <StyledCell
              variant={variant}
              key={cellKey}
              {...cellProps}
              colored={row.isSelected}
              className={(cell.column as FixMeAny).className}
              hasSelected={hasSelected}
              style={{
                ...cellProps.style,
                flex: isCheckboxColumn ? undefined : cellProps.style?.flex,
                display: isCheckboxColumn ? 'grid' : cellProps.style?.display,
                alignItems: isCheckboxColumn ? 'center' : cellProps.style?.alignItems,
                justifyContent: isCheckboxColumn ? 'center' : cellProps.style?.justifyContent,
              }}
            >
              {cell.render('Cell')}
            </StyledCell>
          )
        })}
      </>
    )
  }

  const paginationProps = {
    isLoading,
    isFetching,
    isVisible: isPaginationVisible,
    lastPage,
    onChangePageNumber: handleChangePageNumber,
    onChangePageSize: handleChangePageSize,
    pageNumber,
    pageSize,
    rowsCount: page.length,
    totalDataCount,
    borderless,
  }

  const rowItems = rowVirtualizer.getVirtualItems()
  const hasSelected = rows.some((i) => i.isSelected)

  return (
    <StyledTable
      borderless={borderless}
      style={
        fillContainerHeight ? { ...style, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : style
      }
      variant={variant}
    >
      {bulkOperationProps.selectedRowsCount > 0 && !isLoading && <BulkOperationPanel {...bulkOperationProps} />}
      <div
        {...getTableProps()}
        ref={tableRef}
        style={fillContainerHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}
      >
        <TableHeader
          variant={variant}
          ref={theadRef}
          tHeadClassName={selectedFlatRows.length > 0 || (preSelectedIds && selectedRowCount) ? 'bulk-action' : ''}
          withCheckbox={withCheckbox && !isLoading}
          headerGroups={headerGroups}
          onSort={onSort}
          initialSortBy={
            initialState.sortBy && {
              id: initialState.sortBy[0].id,
              order: initialState.sortBy[0].desc ? 'desc' : 'asc',
            }
          }
          borderless={borderless}
          scrollLeft={headerScroll}
        />
        <div
          data-testid="table-body-container"
          ref={tableBodyRef}
          {...getTableBodyProps()}
          style={
            fillContainerHeight
              ? { flex: 1, minHeight: 0, overflow: 'auto' }
              : {
                  height: isInlineTable(variant) ? '500px' : getHeight(),
                  overflow: isInlineTable(variant) ? 'auto' : 'auto',
                }
          }
        >
          {error ? (
            <ErrorView error={error} />
          ) : (
            <>
              {isEmpty ? (
                <>
                  {emptyView ?? (
                    // fillContainerHeight consumers (the drilldown modals) flex the body to the
                    // space that is left; a fixed px height here overflows it on short windows.
                    <EmptyResultView
                      height={fillContainerHeight ? '100%' : getHeight()}
                      message={emptyText}
                      isOnSearch={isOnSearch}
                    />
                  )}
                </>
              ) : (
                <div
                  data-testid="table-body-wrapper-container"
                  style={{
                    height: rowVirtualizer.getTotalSize(),
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  <div
                    data-testid="table-body-wrapper"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${rowItems[0]?.start ?? 0}px)`,
                    }}
                  >
                    {rowItems.map((rowItem) => {
                      const row = page[rowItem.index]
                      prepareRow(row)
                      const { key, ...rowProps } = row.getRowProps({
                        style: {
                          minWidth: '100%',
                          ...getTrStyles?.(row),
                        },
                      })
                      return (
                        <StyledRow
                          key={key}
                          {...rowProps}
                          data-index={rowItem.index}
                          ref={rowVirtualizer.measureElement}
                          className={`${rowItem.index % 2 === 0 ? 'row-even' : 'row-odd'} ${
                            rowItem.index === activeRowIndex ? 'row-selected' : ''
                          }`}
                          onClick={onRowClick && !isLoading ? () => onRowClick?.(row.original) : undefined}
                          height={rowHeight}
                        >
                          {renderRow({ index: rowItem.index })}
                        </StyledRow>
                      )
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Pagination variant={variant} {...paginationProps} />
    </StyledTable>
  )
}

export default Table
